/**
 * M11b: localStorage helper for carrying the pending advisor invite token
 * across the auth flow (register/login → email verify → dashboard → accept panel).
 *
 * The token arrives in the mail link as `?invite=<token>` on `/register` or
 * `/login`. Because the auth flow redirects through `/verify-email` and then to
 * `/dashboard`, the token must be persisted client-side so the accept panel on
 * `/dashboard/konto/steuerberater` can still read it.
 *
 * Security note: the token is the same value that was delivered in the
 * invitation email URL, so placing it in localStorage for a short time does not
 * increase the attack surface beyond what is already present.  It MUST be
 * cleared after successful acceptance (or explicit decline) via
 * {@link clearPendingInviteToken}.
 *
 * INERT guarantee: every function returns null / is a no-op when
 * `REACT_APP_ADVISOR_EMAIL_INVITE_ENABLED` is not `'true'`, so the entire
 * mechanism stays invisible for normal users.
 */

const PENDING_INVITE_TOKEN_KEY = 'pendingAdvisorInviteToken';

/**
 * FE feature flag (CRA env, fail-closed default off).
 * Mirrors the flag in advisorInviteApi.ts — kept in sync here to avoid a
 * cross-module import cycle (this module is imported by pages + MainLayout;
 * advisorInviteApi is imported by AdvisorInviteAcceptPanel).
 */
const ADVISOR_EMAIL_INVITE_ENABLED =
  process.env.REACT_APP_ADVISOR_EMAIL_INVITE_ENABLED === 'true';

/**
 * Returns the pending invite token stored in localStorage, or null if none
 * exists, the flag is off, or localStorage is unavailable.
 */
export function getPendingInviteToken(): string | null {
  if (!ADVISOR_EMAIL_INVITE_ENABLED) return null;
  try {
    return localStorage.getItem(PENDING_INVITE_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Stores the invite token in localStorage for retrieval after the auth redirect
 * chain completes.  No-op when the flag is off or the token is falsy.
 *
 * @param token - the plaintext token from the `?invite=` URL parameter.
 */
export function setPendingInviteToken(token: string): void {
  if (!ADVISOR_EMAIL_INVITE_ENABLED || !token) return;
  try {
    localStorage.setItem(PENDING_INVITE_TOKEN_KEY, token);
  } catch {
    // Silently ignore (private-browsing storage quota / unavailable)
  }
}

/**
 * Removes the pending invite token from localStorage.  Must be called after
 * the invitation has been successfully accepted so subsequent navigations do
 * not trigger the portal redirect again.
 */
export function clearPendingInviteToken(): void {
  if (!ADVISOR_EMAIL_INVITE_ENABLED) return;
  try {
    localStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
  } catch {
    // Silently ignore
  }
}
